// Libraries
import { Box, Collapse, Flex, Table, Tbody, Td, Text, Tfoot, Thead, Tr } from '@chakra-ui/react';
import { Fragment, Suspense } from 'react';
// Components
import { WarningIcon } from '@atoms/index';
import { TableCell, TableHeader } from './Components/index';
// Utils
import text from '@translations/en.json';
// Types
import { Cell } from '@tanstack/react-table';
import { TableLayoutProps } from './TableLayout.types';

/**
 * Determines the background color for striped rows in the table.
 *
 * @param {number} num - The row number.
 * @returns {string} The background color for striped rows ('table.footer' or 'none').
 */
const striped = (num: number) => (num % 2 !== 0 ? 'table.footer' : 'none');

/**
 * FleetRatingsTableLayout component displays the layout of a fleet ratings table.
 *
 * @component
 * @param {EeoiScoresTableLayoutProps} props - The component props.
 * @param {Table<EeoiScoresBody>} props.table - Is an instance of the `Table` component with `EeoiVessel` as the table row type.
 * @param {cellsWithBorderRightMap} props.cellsWithBorderRightMap - Responsive map of all cell columns with border right.
 * @param {rowExpandedChildren} props.rowExpandedChildren - Children component that could be render in the expanded row.
 * @param {ReactElement} props.footer - The footer element to display below the table.
 * @param {string|number} props.h - The height of the table.
 * @param {string|number} props.maxH - The max height of the table.
 * @returns {ReactElement} FleetRatingsTableLayout component.
 */
export const TableLayout = <TableBody, CellsName>({
  table,
  cellsWithBorderRightMap,
  rowExpandedChildren: RowExpandedChildren,
  footer,
  // TODO: last minute change
  // h = 'calc(100vh - 5.625rem)',
  maxH,
  noResultsIcon = true,
  noResultsMessage = text.no_table_results,
}: TableLayoutProps<TableBody, CellsName>) => {
  const columnsCount = table.getRowModel().rows.length;
  const minH = columnsCount > 1 ? 61 : 44;
  const noResults = columnsCount === 0;

  return (
    <Box borderTop='section' borderRadius='none' overflowY='auto' maxH={maxH} minH={minH}>
      <Table position='relative'>
        {/* Render the table header */}
        <Thead position='sticky' top={0} zIndex='docked'>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr
              key={headerGroup.id}
              border='none'
              _first={{ base: { display: 'none' }, sm: { backgroundColor: 'white', display: 'table-row' } }}
              _last={{ backgroundColor: 'table.header' }}
            >
              {headerGroup.headers.map((header) => (
                <TableHeader<TableBody, CellsName>
                  header={header}
                  key={header.id}
                  cellsWithBorderRightMap={cellsWithBorderRightMap}
                />
              ))}
            </Tr>
          ))}
        </Thead>
        {/* Render the table body */}
        <Tbody>
          {table.getRowModel().rows.map((row, index) => {
            return (
              <Fragment key={`${row.id}-fragment`}>
                <Tr
                  key={row.id}
                  h={16}
                  backgroundColor={striped(index)}
                  _last={{ borderBottom: 'none' }}
                  border='none'
                  borderBottom='section'
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      cell={cell as Cell<unknown, unknown>}
                      key={cell.id}
                      cellsWithBorderRightMap={cellsWithBorderRightMap}
                      _last={{
                        maxW: 20,
                      }}
                    />
                  ))}
                </Tr>
                {/* Collapsible row */}
                {row.getCanExpand() && RowExpandedChildren && (
                  <Tr key={`${row.id}_expanded_row`} backgroundColor='table.expandRow' border='none'>
                    <Td
                      colSpan={row.getVisibleCells().length}
                      padding={0}
                      border='none'
                      overflow='hidden'
                      key={`${row.id}_expanded_cell`}
                    >
                      <Collapse in={row.getIsExpanded()} animateOpacity key={`${row.id}_expanded_collapse_container`}>
                        <Suspense>
                          <RowExpandedChildren {...row.original} />
                        </Suspense>
                      </Collapse>
                    </Td>
                  </Tr>
                )}
              </Fragment>
            );
          })}
        </Tbody>
        {footer && (
          <Tfoot position='sticky' bottom={0} zIndex='modal'>
            <Tr key='table-footer' backgroundColor='table.expandRow' border='none'>
              <Td colSpan={table.getAllColumns().length} padding={0} border='none'>
                {footer}
              </Td>
            </Tr>
          </Tfoot>
        )}
      </Table>
      {noResults && (
        <Flex direction='column' justify='center' align='center' w='full' h={27} opacity={0.7}>
          {noResultsIcon && <WarningIcon boxSize={6} color='text.faded' />}
          <Text color='text.faded' fontSize='m'>
            {noResultsMessage}
          </Text>
        </Flex>
      )}
    </Box>
  );
};
